import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Field, FormSpy } from 'react-final-form';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import isAfter from 'date-fns/isAfter';
import isEqual from 'date-fns/isEqual';
import formatISO9075 from 'date-fns/formatISO9075';
import _debounce from 'lodash/debounce';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import AutocompleteFieldAdapter from 'components/shared/AutocompleteFieldAdapter';
import DatePickerFieldAdapter from 'components/shared/DatePickerFieldAdapter';
import { date } from 'common/validations';
import { reportActions } from 'store/modules/report';
import { materialActions } from 'store/modules/material';
import { supplierActions } from 'store/modules/supplier';
import { orderActions } from 'store/modules/order';
import { reportType, debounceWait } from 'common/constants';
import { filtersPagination } from 'common/filters';

const useStyles = makeStyles((theme) => ({
  title: {
    marginBottom: theme.spacing(3),
  },
  description: {
    marginBottom: theme.spacing(3),
  },
  subtitle: {
    marginBottom: theme.spacing(2),
  },
  accordionRoot: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  diviver: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    marginTop: theme.spacing(4),
    textAlign: 'right',
  },
}));

const initialValues = {
  start_date: null,
  end_date: null,
  materials_id: [],
  suppliers_id: [],
  purchase_orders_id: [],
};

const EntryForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [formState, setFormState] = useState(null);

  const {
    actionLoading,
    authUser,
    materialOptions,
    materialLoading,
    supplierOptions,
    supplierLoading,
    orderOptions,
    orderLoading,
  } = useSelector((state) => ({
    actionLoading: state.report.loading.action,
    authUser: state.auth.user,
    materialOptions: state.material.options,
    materialLoading: state.material.loading.filter,
    supplierOptions: state.supplier.options,
    supplierLoading: state.supplier.loading.filter,
    orderOptions: state.order.options,
    orderLoading: state.order.loading.filter,
  }));

  const disableStartDate = (day) => {
    const { values: { end_date } } = formState;

    if (end_date) {
      const difference = differenceInCalendarDays(day, end_date);
      const isEqualOrAfter = isEqual(day, end_date) || isAfter(day, end_date);

      return difference > 62 || isEqualOrAfter;
    }

    return false;
  };

  const disableEndDate = (day) => {
    const { values: { start_date } } = formState;

    if (start_date) {
      const difference = differenceInCalendarDays(day, start_date);

      return difference > 62 || difference <= 0;
    }

    return false;
  };

  const validate = (values) => {
    const errors = {};

    if (!date(values.start_date)) {
      errors.start_date = 'Este campo no puede estar vacío';
    }

    if (!date(values.end_date)) {
      errors.end_date = 'Este campo no puede estar vacío';
    }

    return errors;
  };

  const onMaterialIdChange = (event, value, reason) => {
    if (reason === 'input' && value.length >= 2) {
      dispatch(materialActions.getFilterOptions({ value, pagination: filtersPagination }));
    }
  };

  const onSupplierIdChange = (event, value, reason) => {
    if (reason === 'input' && value.length >= 2) {
      dispatch(supplierActions.getFilterOptions({ value, pagination: filtersPagination }));
    }
  };

  const onOrderIdChange = (event, value, reason) => {
    if (reason === 'input' && value.length >= 2) {
      dispatch(orderActions.getFilterOptions({ value, pagination: filtersPagination }));
    }
  };

  const onSubmit = (values) => {
    let fileData = {
      start_date: formatISO9075(values.start_date),
      end_date: formatISO9075(values.end_date),
    };

    if ('materials_id' in values && values.materials_id.length > 0) {
      fileData = {
        ...fileData,
        materials_id: values.materials_id.map((material) => Number(material.value)),
      };
    }

    if ('suppliers_id' in values && values.suppliers_id.length > 0) {
      fileData = {
        ...fileData,
        suppliers_id: values.suppliers_id.map((supplier) => Number(supplier.value)),
      };
    }

    if ('purchase_orders_id' in values && values.purchase_orders_id.length > 0) {
      fileData = {
        ...fileData,
        purchase_orders_id: values.purchase_orders_id.map((order) => Number(order.value)),
      };
    }

    const createData = {
      author_id: Number(authUser.id),
      type: reportType.entry,
    };

    dispatch(reportActions.createReport({ createData, fileData }));
  };

  return (
    <Form
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit}
      render={({
        handleSubmit,
        invalid,
      }) => (
        <form
          onSubmit={handleSubmit}
        >
          <FormSpy onChange={(state) => setFormState(state)} />
          <Typography className={classes.title} variant="h5">Reporte de entradas de materiales</Typography>
          <Typography className={classes.description} variant="body2">
            Este reporte genera una lista de las entradas de materiales que cumplan con los
            filtros seleccionados. El m&aacute;ximo rango de fecha a seleccionar corresponde a 62
            d&iacute;as calendario.
          </Typography>
          <Divider className={classes.diviver} />
          <Typography className={classes.subtitle} variant="h6">Filtros</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Field
                id="start_date-create"
                name="start_date"
                component={DatePickerFieldAdapter}
                label="Desde"
                disableFuture
                shouldDisableDate={disableStartDate}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Field
                id="end_date-create"
                name="end_date"
                component={DatePickerFieldAdapter}
                label="Hasta"
                disableFuture
                shouldDisableDate={disableEndDate}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                id="materials_id-create"
                name="materials_id"
                component={AutocompleteFieldAdapter}
                options={materialOptions}
                isMultiple
                filterSelectedOptions
                loading={materialLoading}
                onInputChange={_debounce(onMaterialIdChange, debounceWait)}
                textProps={{
                  label: 'Materiales',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                id="suppliers_id-create"
                name="suppliers_id"
                component={AutocompleteFieldAdapter}
                options={supplierOptions}
                isMultiple
                filterSelectedOptions
                loading={supplierLoading}
                onInputChange={_debounce(onSupplierIdChange, debounceWait)}
                textProps={{
                  label: 'Proveedores',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                id="purchase_orders_id-create"
                name="purchase_orders_id"
                component={AutocompleteFieldAdapter}
                options={orderOptions}
                isMultiple
                filterSelectedOptions
                loading={orderLoading}
                onInputChange={_debounce(onOrderIdChange, debounceWait)}
                textProps={{
                  label: 'Ordenes de Compra',
                }}
              />
            </Grid>
          </Grid>
          <div className={classes.buttonContainer}>
            <Button
              type="submit"
              size="small"
              color="primary"
              variant="contained"
              disabled={invalid || actionLoading}
            >
              Generar Reporte
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default EntryForm;
